import { useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import PageMeta from "../../components/common/PageMeta";
import { STYLES, HEAD_STYLESHEETS } from "./PartnerNetwork";
import PublicAuthActions from "./PublicAuthActions";

/**
 * The chrome every standalone public page wears: fonts, scoped styles, the slim header and the
 * minimal footer.
 *
 * <p>Extracted when the public surface grew past two pages. The landing page keeps its own header
 * (it has the nav, the burger menu and the auth buttons) and its own sitemap footer; everything
 * else — contact, pricing, about, trust, the legal pages — is a document with a logo at the top and
 * a way back at the bottom, and five copies of that is five places for it to drift.</p>
 */

export const BRAND = "#2C7A5B";

export default function PublicPageShell({
  title,
  description,
  children,
  maxWidth = 760,
}: {
  /** Browser title. " | DAconnect" is appended here so no caller has to remember it. */
  title: string;
  description: string;
  children: ReactNode;
  /** Content column. Wider for pages that lay out cards rather than prose. */
  maxWidth?: number;
}) {
  // The fonts and the scoped hover/responsive CSS the pn- classes need. Removed on unmount so a
  // navigation away from the public surface does not leave sheets behind.
  useEffect(() => {
    const nodes: HTMLElement[] = HEAD_STYLESHEETS.map((href) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
      return link;
    });
    const styleEl = document.createElement("style");
    styleEl.textContent = STYLES;
    document.head.appendChild(styleEl);
    nodes.push(styleEl);
    return () => nodes.forEach((n) => n.remove());
    // STYLES is a module constant, so this never re-runs at runtime. It IS in the dependency list
    // so a hot module replacement re-injects the sheet — without it the page keeps whichever CSS
    // was injected when it first mounted, and new markup renders against old rules.
  }, [STYLES]);

  return (
    <div className="pn-root">
      <PageMeta title={`${title} | DAconnect`} description={description} />

      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(255,255,255,0.94)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #E9E5DE",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 24px",
            height: 72,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <Link to="/" style={{ display: "flex", alignItems: "center" }}>
            <img
              src="/images/partner-network/logo.png"
              alt="DAconnect"
              style={{ height: 36, width: "auto", display: "block" }}
            />
          </Link>
          <PublicAuthActions
            gap={10}
            linkStyle={{ textDecoration: "none", color: "#243139", fontWeight: 600, fontSize: 15, padding: "10px 14px", borderRadius: 9 }}
            pillStyle={{
              textDecoration: "none",
              color: "#fff",
              fontWeight: 600,
              fontSize: 15,
              padding: "10px 18px",
              borderRadius: 9,
              background: BRAND,
            }}
            pillClassName="pn-cta"
          />
        </div>
      </header>

      <div style={{ maxWidth, margin: "0 auto", padding: "44px 24px 72px" }}>{children}</div>

      <footer style={{ background: "#18272C", color: "#A8B2C2" }}>
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "22px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            fontSize: 13,
          }}
        >
          <span>© 2026 DAconnect Pty Ltd · ABN 74 168 911 402</span>
          <span style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <Link to="/privacy" className="pn-foot" style={{ color: "inherit", textDecoration: "none" }}>Privacy</Link>
            <Link to="/terms" className="pn-foot" style={{ color: "inherit", textDecoration: "none" }}>Terms</Link>
            <Link to="/contact" className="pn-foot" style={{ color: "inherit", textDecoration: "none" }}>Contact</Link>
            <Link to="/" className="pn-foot" style={{ color: BRAND, fontWeight: 700, textDecoration: "none" }}>
              Back to DAconnect
            </Link>
          </span>
        </div>
      </footer>
    </div>
  );
}

/** A page's h1 and its standfirst, set the same way on every document page. */
export function PageTitle({ title, intro }: { title: string; intro?: string }) {
  return (
    <>
      <h1
        style={{
          fontFamily: "'Archivo',sans-serif",
          fontWeight: 800,
          fontSize: 34,
          letterSpacing: "-0.02em",
          margin: "0 0 10px",
          color: "#243139",
        }}
      >
        {title}
      </h1>
      {intro && (
        <p style={{ fontSize: 16.5, lineHeight: 1.6, color: "#6A7484", margin: "0 0 32px" }}>{intro}</p>
      )}
    </>
  );
}

/** A titled block of prose. */
export function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section style={{ marginBottom: 34 }}>
      <h2
        style={{
          fontFamily: "'Archivo',sans-serif",
          fontWeight: 800,
          fontSize: 20,
          letterSpacing: "-0.01em",
          margin: "0 0 10px",
          color: "#243139",
        }}
      >
        {heading}
      </h2>
      <div style={{ fontSize: 15.5, lineHeight: 1.7, color: "#52606D" }}>{children}</div>
    </section>
  );
}

/**
 * Marks copy that is NOT finished — a legal clause nobody has reviewed, a claim only the business
 * can make.
 *
 * <p>Deliberately loud. A placeholder that looks like finished prose is how draft text ends up
 * published and relied on, and on a page about privacy or terms that is the difference between an
 * unfinished page and a misleading one.</p>
 */
export function NeedsReview({ children }: { children: ReactNode }) {
  return (
    <div
      data-testid="needs-review"
      style={{
        background: "#FFF8E6",
        border: "1px solid #F0D9A0",
        borderLeft: "4px solid #C99A2E",
        borderRadius: 8,
        padding: "12px 14px",
        margin: "14px 0",
        fontSize: 14.5,
        lineHeight: 1.6,
        color: "#6B5514",
      }}
    >
      <strong style={{ display: "block", marginBottom: 4, color: "#5A4610" }}>Needs your review</strong>
      {children}
    </div>
  );
}
